import { IRenderingCommonContext } from "@commonRendering/types";
import { IBreadcrumbLinkData } from "@pageBuilder/typings";
import { IAnimatedWidget } from "../../../typings/widget-style-data";
import { LinkRenderer } from "../link.renderer";
import { WidgetRendererBase } from "../widget-renderer-base";
import { IBreadcrumbsWidgetData } from "src/typings/breadcrumbs-widget-data";
import { BreadcrumbsWidgetStyleRenderer } from "./breadcrumbs-widget-styles-renderer";
import { BreadcrumbsWidgetScriptRenderer } from "./breadcrumbs-widget-script-renderer";

export class BreadcrumbsWidgetRenderer extends WidgetRendererBase<IBreadcrumbsWidgetData> {

    private readonly linkRenderer: LinkRenderer;
    private readonly scriptRenderer: BreadcrumbsWidgetScriptRenderer;
    private readonly breadcrumbsStyleRenderer: BreadcrumbsWidgetStyleRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.linkRenderer = new LinkRenderer();
        this.scriptRenderer = new BreadcrumbsWidgetScriptRenderer();
        this.breadcrumbsStyleRenderer = new BreadcrumbsWidgetStyleRenderer(this.styleService);
    }

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("breadcrumbsWidget");
    }

    protected renderInternal(element: HTMLElement, data: IBreadcrumbsWidgetData): HTMLElement {
        const previousData = this.getData(element);
        if (previousData) {
            this.cleanupItems(element);
        }

        const content = element.querySelector(".widget-content") as HTMLElement;
        const editorLayout = this.getNavigation(data);
        content.appendChild(editorLayout);

        this.scriptRenderer.renderCommon(this.renderedDocument);
        this.renderStyles(element, data);

        return element;
    }

    protected renderStyles(element: HTMLElement, data: IBreadcrumbsWidgetData): void {
        this.renderBaseStyles(element, data.style);
        this.renderBreadcrumbsStyles(element, data);
    }

    private renderBaseStyles(element: HTMLElement, style: IAnimatedWidget): void {
        this.animationRenderer.setAnimationStyles(element, style.animation);
    }

    private renderBreadcrumbsStyles(element: HTMLElement, data: IBreadcrumbsWidgetData): void {
        if (data.breadcrumbsStyle) {
            this.breadcrumbsStyleRenderer.render(element, data.breadcrumbsStyle);
        }
    }

    private getNavigation(data: IBreadcrumbsWidgetData): HTMLElement {
        const wrapper = document.createElement("div");
        const nav = document.createElement("nav");
        wrapper.classList.add("nav-wrapper");

        const ul = this.getEditorBreadcrumbsList(data);
        nav.append(ul);
        wrapper.append(nav);

        return wrapper;
    }

    private getEditorBreadcrumbsList(data: IBreadcrumbsWidgetData): HTMLElement {
        const ul = document.createElement("ul");
        ul.setAttribute("itemscope", "");
        ul.setAttribute("itemtype", "BreadcrumbList - Schema.org Type");
        ul.classList.add("root");
        ul.classList.add(data.layoutType === "leftToRight" ? "left-to-right" : "right-to-left");

        if (!data.link) {
            return ul;
        }

        const items: HTMLElement[] = [];
        this.collectNavigationItems(items, data.link);

        items.reverse().forEach((item, index) => {
            const meta = item.querySelector("meta[itemprop=\"position\"]");
            if (meta) {
                meta.setAttribute("content", (index).toString());
            }
        });

        if (data.layoutType === "rightToLeft") {
            items.reverse();
        }

        items.forEach((item, index) => {
            ul.append(item);

            if (index < items.length - 1 && data.includeSeparator) {
                const arrow = document.createElement("span");
                arrow.classList.add("arrow", "item-icon");
                ul.append(arrow);
            }
        });

        return ul;
    }

    private collectNavigationItems(items: HTMLElement[], link: IBreadcrumbLinkData): void {
        const li = this.getNavigationItem(link, !link.isActive);
        items.push(li);

        if (link.parentLink) {
            this.collectNavigationItems(items, link.parentLink);
        }
    }

    private getNavigationItem(item: IBreadcrumbLinkData | null = null, includeHref = true): HTMLElement {
        const li = document.createElement("li");
        li.setAttribute("itemprop", "itemListElement");
        li.setAttribute("itemscope", "");
        li.setAttribute("itemtype", "ListItem - Schema.org Type");

        const span = document.createElement("span");
        span.classList.add("nav-item-name-label");
        span.setAttribute("itemprop", "name");

        const meta = document.createElement("meta");
        meta.setAttribute("itemprop", "position");

        if (item) {
            span.innerHTML = item.text;

            if (includeHref) {
                const a = document.createElement("a") as HTMLAnchorElement;
                a.id = item.id;
                a.classList.add("nav-item-name");
                this.linkRenderer.render(a, item.value);

                a.setAttribute("itemprop", "item");
                a.setAttribute("tabindex", "-1");
                a.append(span);
                a.appendChild(meta);

                li.append(a);
            } else {
                li.append(span);
                li.appendChild(meta);
            }

            if (item.isActive) {
                li.classList.add("active");
            }
        }

        return li;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        //nothing
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        const result = [`#${element.id}`];
        result.push(...this.breadcrumbsStyleRenderer.getStylesSelectors(element.id));
        return result;
    }

    private cleanupItems(element: HTMLElement): void {
        const items = element.querySelectorAll(".nav-wrapper nav ul li");
        items?.forEach(li => {
            li.remove();
        });

        const navigations = element.querySelectorAll<HTMLAnchorElement>(".nav-wrapper");
        navigations.forEach(navigation => {
            navigation?.remove();
        });
    }

}
